import { Box, keyframes, Typography } from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import MicOffIcon from "@mui/icons-material/MicOff";
import { useEffect, useMemo, useRef, type JSX } from "react";
import type { CallRosterParticipant } from "@xop/dashboard-shared";

/** A single live camera tile — binds the MediaStream to its own <video> via a
 * local ref/effect, so adding/removing tiles never detaches another's stream
 * (the dynamic-grid version of CallWindow's setSrcObject effects). */
function ParticipantVideo({ stream, mirror, testid }: { stream: MediaStream; mirror: boolean; testid: string }): JSX.Element {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || el.srcObject === stream) return;
    // Tolerate environments (happy-dom / WKWebView) that reject srcObject or
    // don't autoplay from the attribute — mirror CallWindow's setSrcObject.
    try {
      el.srcObject = stream;
      const p = el.play?.();
      if (p && typeof p.catch === "function") p.catch(() => { /* autoplay/not-ready — ignore */ });
    } catch {
      /* unsupported env / invalid stream — ignore */
    }
  }, [stream]);
  return (
    <video
      ref={ref}
      data-testid={testid}
      autoPlay
      playsInline
      muted
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", ...(mirror ? { transform: "scaleX(-1)" } : {}) }}
    />
  );
}

/**
 * The GROUP-call stage: a responsive avatar grid that makes it instantly clear
 * HOW MANY people are on the call (3, 4, 5, 6, 7, …) and who they are. Replaces
 * the cramped one-line chip strip. Used in place of `VideoStage` whenever the
 * roster has > 2 participants; the 1:1 look is unchanged.
 *
 * Responsive by construction — the grid is `repeat(auto-fit, minmax(tile, 1fr))`
 * so the column count follows the available width: ~2-3 tiles across in the
 * narrow floating window, many across when maximized. Tiles never overflow; the
 * grid scrolls if a big group can't fit the floating window's height.
 *
 * Each tile: a colour-keyed initials avatar, the name (own → "You"), and join
 * state — `joined` is solid, `ringing` is dimmed with a pulsing ring + label.
 * (Per-participant live video is future work; this is the people view.)
 */
const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(255,183,77,0.55); }
  70%  { box-shadow: 0 0 0 8px rgba(255,183,77,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,183,77,0); }
`;

// Deterministic, readable-on-black avatar colours, picked by user id.
const AVATAR_COLORS = ["#5C6BC0", "#26A69A", "#EF5350", "#AB47BC", "#FFA726", "#42A5F5", "#66BB6A", "#EC407A"] as const;
function colorFor(userId: number): string {
  return AVATAR_COLORS[Math.abs(userId) % AVATAR_COLORS.length]!;
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

export function ParticipantGrid({
  participants,
  selfId,
  maximized,
  muted,
  remoteCameras,
  localCameraStream,
}: {
  participants: CallRosterParticipant[];
  selfId: number;
  maximized: boolean;
  muted: boolean;
  /** Remote camera streams keyed by the publisher's conferweb sid. */
  remoteCameras: Map<number, MediaStream>;
  /** Our own camera stream (self tile), or null when our camera is off. */
  localCameraStream: MediaStream | null;
}): JSX.Element {
  const total = participants.length;
  const ringing = participants.filter((p) => p.state === "ringing").length;

  // Assign each remote tile its live camera stream. PRIMARY: match a participant
  // to remoteCameras by their roster conferweb_sid. FALLBACK: if a stream is
  // flowing but its sid isn't on any roster entry yet (the roster-sid plumbing
  // lagged), STILL render it — hand the leftover (unclaimed) streams to the joined
  // remote tiles that didn't get a sid match, in order. So the grid shows the
  // camera the receiver is ALREADY receiving regardless of roster-sid timing —
  // this is the "grid shows initials instead of the live camera" fix. With a
  // single camera on (the common case) the assignment is unambiguous; the sid
  // match keeps a true multi-camera group correct.
  const tileStream = useMemo(() => {
    const map = new Map<number, MediaStream>();
    const claimed = new Set<number>();
    const remotes = participants.filter((p) => p.user_id !== selfId && p.state !== "ringing");
    for (const p of remotes) {
      if (p.conferweb_sid != null && remoteCameras.has(p.conferweb_sid)) {
        map.set(p.user_id, remoteCameras.get(p.conferweb_sid)!);
        claimed.add(p.conferweb_sid);
      }
    }
    const leftover = [...remoteCameras.entries()].filter(([sid]) => !claimed.has(sid)).map(([, s]) => s);
    let li = 0;
    for (const p of remotes) {
      if (!map.has(p.user_id) && li < leftover.length) {
        map.set(p.user_id, leftover[li]!);
        li += 1;
      }
    }
    return map;
  }, [participants, selfId, remoteCameras]);

  // Diagnostic: dump what the grid will render so one steady-state screenshot
  // shows whether each tile resolved a stream (via sid OR the leftover fallback).
  useEffect(() => {
    const rows = participants.map((p) => {
      const isSelf = p.user_id === selfId;
      const has = isSelf ? !!localCameraStream : tileStream.has(p.user_id);
      return `${p.name}[uid=${p.user_id} sid=${p.conferweb_sid ?? "-"}${isSelf ? " SELF" : ""} vid=${has ? "YES" : "no"}]`;
    });
    console.info(`[call] GRID n=${total} remoteCameras=[${[...remoteCameras.keys()]}] :: ${rows.join("  ")}`);
  }, [participants, remoteCameras, localCameraStream, selfId, total, tileStream]);

  // Tile footprint scales with the view; auto-fit then chooses the column count.
  const minTile = maximized ? 150 : 100;
  const avatar = maximized ? 64 : 46;

  return (
    <Box
      data-testid="call-roster"
      sx={{
        position: "relative",
        width: "100%",
        bgcolor: "#000",
        display: "flex",
        flexDirection: "column",
        ...(maximized ? { flex: 1, minHeight: 0 } : { aspectRatio: "16 / 9" }),
      }}
    >
      {/* The headline count — "N people" — so the group size is unmistakable. */}
      <Box
        data-testid="call-roster-count"
        sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.75, px: maximized ? 2 : 1.5, pt: maximized ? 1.5 : 1, pb: 0.5 }}
      >
        <GroupsRoundedIcon sx={{ color: "rgba(255,255,255,0.85)", fontSize: maximized ? 22 : 18 }} />
        <Typography sx={{ color: "common.white", fontSize: maximized ? 16 : 13, fontWeight: 700 }}>
          {total} people
        </Typography>
        {ringing > 0 && (
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: maximized ? 13 : 11 }}>
            · {ringing} ringing…
          </Typography>
        )}
      </Box>

      {/* The grid — auto-fit columns by width, scrolls if a big group overflows. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${minTile}px, 1fr))`,
          gap: maximized ? 1.5 : 1,
          alignContent: "start",
          px: maximized ? 2 : 1.5,
          pb: maximized ? 2 : 1.25,
        }}
      >
        {participants.map((p) => {
          const isSelf = p.user_id === selfId;
          const isRinging = p.state === "ringing";
          // The live camera stream for this tile, if any: own camera for self,
          // else the remote stream keyed by this peer's conferweb sid.
          const stream = isRinging ? null : isSelf ? localCameraStream : (tileStream.get(p.user_id) ?? null);
          return (
            <Box
              key={p.user_id}
              data-testid={`call-participant-${p.user_id}`}
              data-state={p.state}
              data-has-video={stream ? "true" : "false"}
              sx={{
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: maximized ? 1 : 0.75,
                py: maximized ? 2 : 1.25,
                px: 1,
                minHeight: maximized ? 130 : 86,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.06)",
                border: isRinging ? "1px dashed rgba(255,255,255,0.28)" : "1px solid rgba(255,255,255,0.08)",
                opacity: isRinging ? 0.72 : 1,
              }}
            >
              {stream ? (
                <>
                  {/* Live camera fills the tile; a caption strip names the person. */}
                  <Box sx={{ position: "absolute", inset: 0 }}>
                    <ParticipantVideo stream={stream} mirror={isSelf} testid={`call-participant-video-${p.conferweb_sid ?? p.user_id}`} />
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 0.75,
                      py: 0.25,
                      background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))",
                    }}
                  >
                    {isSelf && muted && <MicOffIcon sx={{ fontSize: 13, color: "#ff5252", flexShrink: 0 }} />}
                    <Typography noWrap sx={{ color: "common.white", fontSize: maximized ? 13 : 11, fontWeight: 500 }}>
                      {isSelf ? "You" : p.name}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      width: avatar,
                      height: avatar,
                      borderRadius: "50%",
                      bgcolor: colorFor(p.user_id),
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: maximized ? 22 : 16,
                      flexShrink: 0,
                      ...(isRinging ? { animation: `${pulse} 1.8s ease-out infinite` } : {}),
                    }}
                  >
                    {initials(p.name)}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, maxWidth: "100%" }}>
                    {isSelf && muted && <MicOffIcon sx={{ fontSize: 13, color: "#ff5252", flexShrink: 0 }} />}
                    <Typography
                      noWrap
                      sx={{ color: "common.white", fontSize: maximized ? 14 : 12, fontWeight: 500, maxWidth: maximized ? 170 : 92 }}
                    >
                      {isSelf ? "You" : p.name}
                    </Typography>
                  </Box>

                  {isRinging && (
                    <Typography sx={{ color: "#ffb74d", fontSize: maximized ? 12 : 10 }}>ringing…</Typography>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
